import { useMemo } from 'react';
import { Field } from '@hive/ui';

export interface VenueForm {
  name: string;
  code: string;
  externalRef: string;
  slug: string;
  area: string;
  city: string;
  country: string;
  address: string;
  timezone: string;
  lat: string;
  lng: string;
  capacity: string;
}

export const emptyVenueForm: VenueForm = {
  name: '',
  code: '',
  externalRef: '',
  slug: '',
  area: '',
  city: '',
  country: '',
  address: '',
  timezone: 'Asia/Dubai',
  lat: '',
  lng: '',
  capacity: '',
};

const FALLBACK_ZONES = ['Asia/Dubai', 'Asia/Qatar', 'Asia/Riyadh', 'Asia/Bahrain', 'Asia/Kuwait', 'Asia/Muscat', 'Europe/London', 'UTC'];

function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    return intl.supportedValuesOf?.('timeZone') ?? FALLBACK_ZONES;
  } catch {
    return FALLBACK_ZONES;
  }
}

/** Numbers from the form, or an error message for the first bad one. */
export function venueNumbers(f: VenueForm): { lat?: number; lng?: number; capacity?: number; error?: string } {
  const out: { lat?: number; lng?: number; capacity?: number; error?: string } = {};
  for (const key of ['lat', 'lng', 'capacity'] as const) {
    const raw = f[key].trim();
    if (!raw) continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) return { error: `${key === 'capacity' ? 'Capacity' : key === 'lat' ? 'Latitude' : 'Longitude'} must be a number.` };
    if (key === 'capacity' && (!Number.isInteger(n) || n < 0)) return { error: 'Capacity must be a whole number.' };
    if (key === 'lat' && Math.abs(n) > 90) return { error: 'Latitude must be between -90 and 90.' };
    if (key === 'lng' && Math.abs(n) > 180) return { error: 'Longitude must be between -180 and 180.' };
    out[key] = n;
  }
  if ((out.lat === undefined) !== (out.lng === undefined)) return { error: 'Give both latitude and longitude, or neither.' };
  return out;
}

/** The venue details fields, shared by "Add a venue" and the setup page. */
export function VenueFieldsForm({ form, onChange, showSlug, capacityRequired }: { form: VenueForm; onChange(next: VenueForm): void; showSlug?: boolean; capacityRequired?: boolean }) {
  const zones = useMemo(timeZones, []);
  const set = (key: keyof VenueForm) => (e: { target: { value: string } }) => onChange({ ...form, [key]: e.target.value });
  return (
    <div className="mc-form">
      <div className="mc-grid2">
        <Field id="v-name" label="Venue name" value={form.name} onChange={set('name')} required maxLength={120} />
        <Field id="v-code" label="Venue ID (optional)" placeholder="e.g. IV-DXB-001" value={form.code} onChange={set('code')} maxLength={40} />
        <Field id="v-ref" label="CRM reference (optional)" placeholder="The operator CRM's ID" value={form.externalRef} onChange={set('externalRef')} maxLength={120} />
        {showSlug && (
          <Field id="v-slug" label="Patron page address" placeholder="irish-village" value={form.slug} onChange={set('slug')} pattern="[a-z0-9][a-z0-9\-]{1,58}[a-z0-9]" title="Lower case letters, digits and dashes" maxLength={60} />
        )}
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="v-tz">
            Time zone
          </label>
          <input id="v-tz" className="hv-input" list="v-tz-list" value={form.timezone} onChange={set('timezone')} required />
          <datalist id="v-tz-list">
            {zones.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
        </div>
        <Field
          id="v-capacity"
          label="Licensed capacity (people)"
          inputMode="numeric"
          placeholder="The fire-code limit, e.g. 350"
          value={form.capacity}
          onChange={set('capacity')}
          required={capacityRequired}
        />
      </div>
      <Field id="v-address" label="Address (optional)" value={form.address} onChange={set('address')} maxLength={300} />
      <div className="mc-grid3">
        <Field id="v-area" label="Area" placeholder="Sports City" value={form.area} onChange={set('area')} maxLength={120} />
        <Field id="v-city" label="City" value={form.city} onChange={set('city')} maxLength={120} />
        <Field id="v-country" label="Country" value={form.country} onChange={set('country')} maxLength={120} />
      </div>
      <div className="mc-grid2">
        <Field id="v-lat" label="Latitude (for the map)" inputMode="decimal" placeholder="25.0406" value={form.lat} onChange={set('lat')} />
        <Field id="v-lng" label="Longitude" inputMode="decimal" placeholder="55.2325" value={form.lng} onChange={set('lng')} />
      </div>
    </div>
  );
}
